import { BUSINESS_TZ_OFFSET_MS } from '@nadoutong/contracts';

const USD_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const USD_FINE_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 6,
});

const CNY_VALUE_FORMATTER = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const CNY_MICRO_VALUE_FORMATTER = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 6,
  maximumFractionDigits: 6,
});
export const RATE_MULTIPLIER_INPUT_STEP = '0.0001';
export const RATE_MULTIPLIER_INPUT_MIN = '0.0001';

const COMPACT_FORMATTER = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

export function formatCompact(value: number): string {
  return COMPACT_FORMATTER.format(value);
}

function isValidRate(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

function formatCnyMagnitude(cents: number): string {
  return `¥${CNY_VALUE_FORMATTER.format(Math.abs(cents) / 100)}`;
}

function formatCnyMicroMagnitude(micros: number): string {
  return `¥${CNY_MICRO_VALUE_FORMATTER.format(Math.abs(micros) / 1_000_000)}`;
}

export interface LocalTimestampOptions {
  readonly seconds?: boolean;
  readonly milliseconds?: boolean;
  readonly fallback?: string;
}

const SERVER_UTC_TIMESTAMP_RE =
  /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}(?::\d{2}(?:\.\d{1,6})?)?)$/;
const USD_MICROS_PER_USD = 1_000_000;

export function parseTimestampMs(value: string): number {
  const trimmed = value.trim();
  const match = SERVER_UTC_TIMESTAMP_RE.exec(trimmed);
  if (match) {
    const [, date, time] = match;
    const normalized = `${date}T${time}`.replace(/\.(\d{3})\d+$/, '.$1');
    return Date.parse(`${normalized}Z`);
  }
  return Date.parse(trimmed);
}

/**
 * Format authoritative money fields carried as integer USD cents.
 *
 * `signed` has no default: every call site must choose. Unsigned prints
 * magnitude (`Math.abs`); always-signed also prefixes `+` on positives.
 * Neither is right for a book balance — use the Balance helpers.
 */
export function formatUsdCents(cents: number, signed: boolean): string {
  if (!Number.isFinite(cents)) return '—';
  const base = USD_FORMATTER.format(Math.abs(cents) / 100);
  if (!signed || cents === 0) return base;
  return cents > 0 ? `+${base}` : `-${base}`;
}

export function formatUsdMicros(micros: number, signed: boolean): string {
  if (!Number.isFinite(micros)) return '—';
  const rounded = Math.trunc(micros);
  const base = USD_FINE_FORMATTER.format(Math.abs(rounded) / USD_MICROS_PER_USD);
  if (!signed || rounded === 0) return base;
  return rounded > 0 ? `+${base}` : `-${base}`;
}

export function formatUsdMicrosOrCents(
  usdMicros: number | null | undefined,
  usdCents: number | null | undefined,
  signed: boolean,
): string {
  return formatUsdMicros(usdMicrosFromCentsFallback(usdMicros, usdCents), signed);
}

/**
 * USD book balance: minus when negative, plain otherwise. `signed=true`
 * would shout a `+` on every healthy row (#1528, #2079).
 */
export function formatUsdMicrosBalance(micros: number): string {
  if (!Number.isFinite(micros)) return '—';
  const rounded = Math.trunc(micros);
  const base = USD_FINE_FORMATTER.format(Math.abs(rounded) / USD_MICROS_PER_USD);
  return rounded < 0 ? `-${base}` : base;
}

export function formatUsdMicrosOrCentsBalance(
  usdMicros: number | null | undefined,
  usdCents: number | null | undefined,
): string {
  return formatUsdMicrosBalance(usdMicrosFromCentsFallback(usdMicros, usdCents));
}

/**
 * Format RMB cents for operator-facing primary displays. Internal account
 * adjustments may still render USD credit via the USD helpers.
 */
export function formatCnyCents(cents: number, signed: boolean): string {
  if (!Number.isFinite(cents)) return '—';
  const base = formatCnyMagnitude(cents);
  if (!signed || cents === 0) return base;
  return cents > 0 ? `+${base}` : `-${base}`;
}

function formatCnyBalance(cents: number): string {
  if (!Number.isFinite(cents)) return '—';
  const base = formatCnyMagnitude(cents);
  return cents < 0 ? `-${base}` : base;
}

export function usdCentsToCnyCents(
  usdCents: number,
  cnyPerUsd: number | null | undefined,
): number | null {
  if (!Number.isFinite(usdCents) || !isValidRate(cnyPerUsd)) {
    return null;
  }
  return Math.round(usdCents * cnyPerUsd);
}

export function usdMicrosToCnyCents(
  usdMicros: number,
  cnyPerUsd: number | null | undefined,
): number | null {
  if (!Number.isFinite(usdMicros) || !isValidRate(cnyPerUsd)) {
    return null;
  }
  return Math.round(usdMicros * cnyPerUsd / 10_000);
}

export function usdMicrosToCnyMicros(
  usdMicros: number,
  cnyPerUsd: number | null | undefined,
): number | null {
  if (!Number.isFinite(usdMicros) || !isValidRate(cnyPerUsd)) {
    return null;
  }
  return Math.round(usdMicros * cnyPerUsd);
}

/**
 * Resolve a dual-written micro-USD/cents pair to micro-USD.
 *
 * The `usdMicros !== 0` arm is the historical-row fallback, and it is
 * deliberately kept: the micro-USD columns were added with `DEFAULT 0`, so a
 * pre-cutover balance or ledger row carries `micros = 0` alongside the real
 * amount in cents. Treating that as a true zero would render years of money as
 * nothing. This mirrors `microsFromDual` in `@nadoutong/contracts`.
 *
 * The cost of that arm is an ambiguity: a value that is *genuinely* 0 micros
 * with non-zero cents would be re-inflated to `cents x 10 000` — presenting a
 * fabricated amount. #1656 closed that hole at the source rather than here,
 * because the fallback cannot tell the two cases apart from the outside:
 *
 * - a server that floors an aggregate's cents from that same aggregate's micros
 *   can never emit `micros = 0` with `cents > 0` — `floor(0 / 10000)` is 0. The
 *   contradictory pair was only reachable while cents were summed per row.
 * - the one shape that *omitted* micros — an admin-dashboard seat with no usage
 *   — now emits `costUsdMicros: 0` explicitly, so it no longer reaches this
 *   fallback at all.
 *
 * So on current-shape data the fallback fires only for genuine legacy rows,
 * which is what it is for. Do not "fix" this to a presence check
 * (`usdMicros !== undefined`) without first confirming no legacy row with
 * `micros = 0` and non-zero cents is still readable by the caller.
 */
export function usdMicrosFromCentsFallback(
  usdMicros: number | null | undefined,
  usdCents: number | null | undefined,
): number {
  if (typeof usdMicros === 'number' && Number.isFinite(usdMicros) && usdMicros !== 0) {
    return Math.trunc(usdMicros);
  }
  return Math.trunc(usdCents ?? 0) * 10_000;
}

export function formatUsdCentsAsCny(
  usdCents: number,
  cnyPerUsd: number | null | undefined,
  signed: boolean,
): string {
  const cnyCents = usdCentsToCnyCents(usdCents, cnyPerUsd);
  return cnyCents === null ? '—' : formatCnyCents(cnyCents, signed);
}

/**
 * A balance, where the sign carries meaning but a `+` on the normal case
 * would be noise (#1528).
 *
 * `formatUsdCentsAsCny` renders magnitude — `Math.abs` — unless `signed`
 * is set, and `signed` prefixes `+` on positives too. Neither is right for
 * a funding balance that may legitimately be negative: unsigned prints an
 * overdrawn vendor as a healthy one, and always-signed makes every
 * ordinary row shout. So: minus when negative, plain otherwise.
 */
export function formatUsdCentsAsCnyBalance(
  usdCents: number,
  cnyPerUsd: number | null | undefined,
): string {
  const cnyCents = usdCentsToCnyCents(usdCents, cnyPerUsd);
  return cnyCents === null ? '—' : formatCnyBalance(cnyCents);
}

export function formatUsdMicrosAsCny(
  usdMicros: number,
  cnyPerUsd: number | null | undefined,
  signed: boolean,
): string {
  const cnyMicros = usdMicrosToCnyMicros(usdMicros, cnyPerUsd);
  if (cnyMicros === null) return '—';
  const base = formatCnyMicroMagnitude(cnyMicros);
  if (!signed || cnyMicros === 0) return base;
  return cnyMicros > 0 ? `+${base}` : `-${base}`;
}

/**
 * Primary money display for spend, KPI cards, funding capacity, and face
 * values. This intentionally rounds metered USD micros to CNY cents.
 *
 * Book balances must not use this helper: unsigned hides an overdraft
 * (#2079). Use `formatUsdMicrosAsCnyDisplayBalance`.
 */
export function formatUsdMicrosAsCnyDisplay(
  usdMicros: number,
  cnyPerUsd: number | null | undefined,
  signed: boolean,
): string {
  const cnyCents = usdMicrosToCnyCents(usdMicros, cnyPerUsd);
  return cnyCents === null ? '—' : formatCnyCents(cnyCents, signed);
}

/**
 * CNY display for a book balance or portfolio net (#2079). Minus when
 * negative, plain otherwise — the same rule as `formatUsdCentsAsCnyBalance`.
 */
export function formatUsdMicrosAsCnyDisplayBalance(
  usdMicros: number,
  cnyPerUsd: number | null | undefined,
): string {
  const cnyCents = usdMicrosToCnyCents(usdMicros, cnyPerUsd);
  return cnyCents === null ? '—' : formatCnyBalance(cnyCents);
}

export function formatUsdMicrosOrCentsAsCnyDisplay(
  usdMicros: number | null | undefined,
  usdCents: number | null | undefined,
  cnyPerUsd: number | null | undefined,
  signed: boolean,
): string {
  return formatUsdMicrosAsCnyDisplay(
    usdMicrosFromCentsFallback(usdMicros, usdCents),
    cnyPerUsd,
    signed,
  );
}

export function formatUsdMicrosOrCentsAsCnyDisplayBalance(
  usdMicros: number | null | undefined,
  usdCents: number | null | undefined,
  cnyPerUsd: number | null | undefined,
): string {
  return formatUsdMicrosAsCnyDisplayBalance(
    usdMicrosFromCentsFallback(usdMicros, usdCents),
    cnyPerUsd,
  );
}

/**
 * Metered spend display for request rows, usage drilldowns, and billing
 * diagnostics where sub-cent precision is part of the evidence.
 */
export function formatUsdMicrosOrCentsAsCnyMetered(
  usdMicros: number | null | undefined,
  usdCents: number | null | undefined,
  cnyPerUsd: number | null | undefined,
  signed: boolean,
): string {
  return formatUsdMicrosAsCny(usdMicrosFromCentsFallback(usdMicros, usdCents), cnyPerUsd, signed);
}

/**
 * Back-compat alias for older imports. New UI code should choose Display or
 * Metered explicitly.
 */
export function formatUsdMicrosOrCentsAsCny(
  usdMicros: number | null | undefined,
  usdCents: number | null | undefined,
  cnyPerUsd: number | null | undefined,
  signed: boolean,
): string {
  return formatUsdMicrosOrCentsAsCnyMetered(usdMicros, usdCents, cnyPerUsd, signed);
}

export function formatCnyRateFromUsdRate(
  usdRate: number | null | undefined,
  cnyPerUsd: number | null | undefined,
): string {
  if (typeof usdRate !== 'number' || !Number.isFinite(usdRate) || !isValidRate(cnyPerUsd)) {
    return '—';
  }
  return `¥${CNY_VALUE_FORMATTER.format(Math.abs(usdRate * cnyPerUsd))}`;
}

export function formatDisplayRateHint(cnyPerUsd: number | null | undefined): string {
  if (!isValidRate(cnyPerUsd)) {
    return '—';
  }
  return `1 USD = ¥${cnyPerUsd.toFixed(2)}`;
}

export function formatRateMultiplier(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '—';
  }
  return value.toFixed(4);
}

/**
 * Format very small costs that are still transported as USD cents.
 */
export function formatFineUsdCents(cents: number): string {
  if (!Number.isFinite(cents)) return '—';
  return USD_FINE_FORMATTER.format(Math.abs(cents) / 100);
}

export type ParseAmountToMinorUnitsResult =
  | { ok: true; minorUnits: number; normalized: string }
  | { ok: false };

/**
 * Parse an operator-entered decimal amount into signed integer minor
 * units (USD cents / CNY fen — any two-decimal currency). Renamed from
 * `parseUsdInputToCents` (#1098): receipts feed it CNY amounts too.
 */
export function parseAmountInputToMinorUnits(raw: string): ParseAmountToMinorUnitsResult {
  const trimmed = raw.trim();
  if (!/^-?(?:\d+|\d+\.\d{1,2}|\.\d{1,2})$/.test(trimmed)) {
    return { ok: false };
  }
  const sign = trimmed.startsWith('-') ? -1 : 1;
  const unsigned = sign === -1 ? trimmed.slice(1) : trimmed;
  const [wholeRaw = '', fracRaw = ''] = unsigned.split('.');
  const whole = wholeRaw.length === 0 ? 0 : Number(wholeRaw);
  const frac = Number(fracRaw.padEnd(2, '0'));
  if (!Number.isSafeInteger(whole) || !Number.isSafeInteger(frac)) {
    return { ok: false };
  }
  const minorUnits = sign * (whole * 100 + frac);
  if (!Number.isSafeInteger(minorUnits)) {
    return { ok: false };
  }
  const normalizedMagnitude = `${whole}.${fracRaw.padEnd(2, '0')}`;
  return {
    ok: true,
    minorUnits,
    normalized: `${sign === -1 ? '-' : ''}${normalizedMagnitude}`,
  };
}

/**
 * Render an ISO timestamp in the browser's local time.
 */
export function formatLocalTimestamp(
  iso: string | null | undefined,
  options: LocalTimestampOptions = {},
): string {
  if (typeof iso !== 'string' || iso.length === 0) {
    return options.fallback ?? '—';
  }
  const ms = parseTimestampMs(iso);
  if (!Number.isFinite(ms)) {
    return options.fallback ?? iso;
  }
  const d = new Date(ms);
  const time = [
    d.getFullYear(),
    pad2(d.getMonth() + 1),
    pad2(d.getDate()),
  ].join('-') + ` ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  if (options.seconds !== true && options.milliseconds !== true) {
    return time;
  }
  const withSeconds = `${time}:${pad2(d.getSeconds())}`;
  return options.milliseconds === true
    ? `${withSeconds}.${String(d.getMilliseconds()).padStart(3, '0')}`
    : withSeconds;
}

export function formatLocalDate(iso: string | null | undefined): string {
  if (typeof iso !== 'string' || iso.length === 0) return '—';
  const ms = parseTimestampMs(iso);
  if (!Number.isFinite(ms)) return iso;
  const d = new Date(ms);
  return [
    d.getFullYear(),
    pad2(d.getMonth() + 1),
    pad2(d.getDate()),
  ].join('-');
}

/**
 * The most recent `count` UTC+8 calendar-month keys ('YYYY-MM'), newest
 * first — the settlement-period selector for the commission report +
 * leaderboard surfaces (epic #1034 E5-M5, #1147). Matches the canonical
 * UTC+8 period the accrual + `commissionPeriodWindow` use.
 */
export function recentPeriodKeys(count: number): string[] {
  const shifted = new Date(Date.now() + 8 * 60 * 60 * 1000);
  const year = shifted.getUTCFullYear();
  const month = shifted.getUTCMonth();
  const keys: string[] = [];
  for (let i = 0; i < count; i += 1) {
    const d = new Date(Date.UTC(year, month - i, 1));
    keys.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
  }
  return keys;
}

export function formatLocalDateTimeInputValue(value: string | null | undefined): string {
  if (typeof value !== 'string' || value.length === 0) return '';
  const ms = parseTimestampMs(value);
  if (!Number.isFinite(ms)) return '';
  const d = new Date(ms);
  return [
    d.getFullYear(),
    pad2(d.getMonth() + 1),
    pad2(d.getDate()),
  ].join('-') + `T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

export function localDateTimeInputValueToIso(value: string): string {
  if (value.length === 0) return '';
  const ms = Date.parse(value);
  if (!Number.isFinite(ms)) return '';
  return new Date(ms).toISOString().replace(/\.\d{3}Z$/, 'Z');
}

function formatUtc8WallClock(ms: number, withTime: boolean): string {
  const d = new Date(ms + BUSINESS_TZ_OFFSET_MS);
  const date = [d.getUTCFullYear(), pad2(d.getUTCMonth() + 1), pad2(d.getUTCDate())].join('-');
  if (!withTime) return date;
  return `${date} ${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`;
}

/**
 * Render an ISO timestamp in welded UTC+8 (Asia/Shanghai), independent
 * of the browser timezone. Used by the Customer 360 usage tab whose
 * buckets are cut on Beijing midnight.
 */
export function formatUtc8Timestamp(
  iso: string | null | undefined,
  options: LocalTimestampOptions = {},
): string {
  if (typeof iso !== 'string' || iso.length === 0) {
    return options.fallback ?? '—';
  }
  const ms = parseTimestampMs(iso);
  if (!Number.isFinite(ms)) {
    return options.fallback ?? iso;
  }
  const wall = formatUtc8WallClock(ms, true);
  if (options.seconds !== true && options.milliseconds !== true) {
    return wall;
  }
  const d = new Date(ms + BUSINESS_TZ_OFFSET_MS);
  const withSeconds = `${wall}:${pad2(d.getUTCSeconds())}`;
  return options.milliseconds === true
    ? `${withSeconds}.${String(d.getUTCMilliseconds()).padStart(3, '0')}`
    : withSeconds;
}

/** `datetime-local` wall clock for a UTC instant, interpreted in UTC+8. */
export function formatUtc8DateTimeInputValue(value: string | null | undefined): string {
  if (typeof value !== 'string' || value.length === 0) return '';
  const ms = parseTimestampMs(value);
  if (!Number.isFinite(ms)) return '';
  return formatUtc8WallClock(ms, true).replace(' ', 'T');
}

/**
 * Parse a `datetime-local` value as UTC+8 wall clock, not the browser
 * timezone. `2026-08-20T00:00` is Beijing midnight.
 */
export function utc8DateTimeInputValueToIso(value: string): string {
  if (value.length === 0) return '';
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})(?::(\d{2}))?$/.exec(value);
  if (match === null) return '';
  const date = match[1];
  const hm = match[2];
  const seconds = match[3] ?? '00';
  if (date === undefined || hm === undefined) return '';
  const ms = Date.parse(`${date}T${hm}:${seconds}+08:00`);
  if (!Number.isFinite(ms)) return '';
  return new Date(ms).toISOString().replace(/\.\d{3}Z$/, 'Z');
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}
